"use client";

import { ArrowRight, Sprout, Droplets, Leaf, Scissors, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Integration() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: lineRef,
    isVisible: lineVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const steps = [{
    number: "01",
    icon: Sprout,
    title: "Network Foundation",
    description: "High-speed fiber and copper infrastructure deployment.",
    duration: "00:00–02:00 HRS"
  }, {
    number: "02",
    icon: Droplets,
    title: "Security Layer",
    description: "AI-powered surveillance and access control integration.",
    duration: "02:00–03:30 HRS"
  }, {
    number: "03",
    icon: Leaf,
    title: "Communication",
    description: "Feature-rich VoIP systems with internal video calling.",
    duration: "03:30–05:00 HRS"
  }, {
    number: "04",
    icon: Scissors,
    title: "Automation",
    description: "Smart building controls and digital signage management.",
    duration: "05:00–05:30 HRS"
  }, {
    number: "05",
    icon: Truck,
    title: "Optimization",
    description: "Final system tuning and performance verification.",
    duration: "05:30–06:00 HRS"
  }];
  return <section data-section-id="5264" id="integration-process" className="bg-background text-foreground relative overflow-hidden py-24 md:py-32 lg:py-40 px-4 md:px-8">
      {}
      <div className="absolute top-12 left-0 right-0 flex items-center pointer-events-none opacity-30">
        <div className="flex-1 flex items-center justify-between">
          {Array.from({
          length: 40
        }).map((_, i) => <div key={i} data-index={i} className="w-px bg-border" style={{
          height: i % 5 === 0 ? "12px" : "6px"
        }} />)}
        </div>
      </div>
      <div className="absolute bottom-12 left-0 right-0 flex items-center pointer-events-none opacity-30">
        <div className="flex-1 flex items-center justify-between">
          {Array.from({
          length: 40
        }).map((_, i) => <div key={i} data-index={i} className="w-px bg-border" style={{
          height: i % 5 === 0 ? "12px" : "6px"
        }} />)}
        </div>
      </div>

      <div className="max-w-7xl mx-auto relative">
        {}
        <div ref={headerRef} className={`text-center mb-20 md:mb-28 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="font-mono text-xs md:text-sm tracking-[0.2em] text-muted-foreground mb-6">
            [ INTEGRATION PROCESS ]
          </p>
          <h2 className="font-serif font-default text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-foreground mb-6">
            Unified Solutions for <em className="italic text-primary">Modern Businesses</em>
          </h2>
          <p className="font-mono text-sm md:text-base text-muted-foreground tracking-wide">
            Seamless performance, security, and scalability.
          </p>
        </div>

        {}
        <div ref={lineRef} className="hidden lg:block relative">
          {}
          <div className="absolute top-[88px] left-[10%] right-[10%] h-px bg-border" />
          {}
          <div className="absolute top-[88px] left-[10%] h-px bg-primary transition-all duration-[2000ms] ease-out" style={{
          width: lineVisible ? "80%" : "0%"
        }} />

          <div className="grid grid-cols-5 gap-6 relative">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className={`flex flex-col items-center text-center transition-all duration-700 ease-out`} style={{
              transitionDelay: `${i * 200}ms`,
              opacity: lineVisible ? 1 : 0,
              transform: lineVisible ? "translateY(0)" : "translateY(20px)"
            }}>
                  {}
                  <div className="font-mono text-sm tracking-[0.2em] text-muted-foreground mb-6">
                    {step.number}
                  </div>

                  {}
                  <div className="relative mb-8">
                    <div className={`w-5 h-5 rounded-lg border-2 border-primary transition-all duration-500 ${lineVisible ? "bg-primary" : "bg-background"}`} style={{
                  transitionDelay: `${i * 200 + 400}ms`
                }} />
                  </div>

                  {}
                  <Icon className="w-8 h-8 text-foreground mb-6" strokeWidth={1} />

                  {}
                  <h3 className="font-serif font-default text-2xl xl:text-[28px] leading-tight text-foreground mb-4">
                    {step.title}
                  </h3>

                  {}
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-[200px]">
                    {step.description}
                  </p>

                  {}
                  <div className="font-mono text-[10px] tracking-[0.15em] text-foreground/80 border border-border px-3 py-1.5 rounded-md">
                    {step.duration}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="lg:hidden relative">
          {}
          <div className="absolute top-2 bottom-2 left-[19px] w-px bg-border" />
          {}
          <div className="absolute top-2 left-[19px] w-px bg-primary transition-all duration-[2000ms] ease-out" style={{
          height: lineVisible ? "calc(100% - 16px)" : "0%"
        }} />

          <div className="space-y-12">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div key={i} data-index={i} className="relative pl-14 transition-all duration-700 ease-out" style={{
              transitionDelay: `${i * 150}ms`,
              opacity: lineVisible ? 1 : 0,
              transform: lineVisible ? "translateX(0)" : "translateX(-20px)"
            }}>
                  {}
                  <div className={`absolute left-[11px] top-2 w-5 h-5 rounded-lg border-2 border-primary transition-all duration-500 ${lineVisible ? "bg-primary" : "bg-background"}`} style={{
                transitionDelay: `${i * 150 + 400}ms`
              }} />

                  <div className="flex items-center gap-3 mb-3">
                    <span className="font-mono text-sm tracking-[0.2em] text-muted-foreground">
                      {step.number}
                    </span>
                    <Icon className="w-5 h-5 text-foreground" strokeWidth={1} />
                  </div>

                  <h3 className="font-serif font-default text-2xl md:text-3xl leading-tight text-foreground mb-3">
                    {step.title}
                  </h3>

                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-4 max-w-md">
                    {step.description}
                  </p>

                  <div className="font-mono text-[10px] tracking-[0.15em] text-foreground/80 border border-border px-3 py-1.5 rounded-md inline-block">
                    {step.duration}
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div ref={ctaRef} className={`mt-20 md:mt-28 text-center transition-all duration-1000 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Button asChild variant="link" className="text-foreground hover:text-primary font-mono text-sm tracking-[0.15em] uppercase no-underline hover:no-underline group">
            <Link to="/SolutionsPage">
              Explore our full solutions
              <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}